import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import supportTicketService from '../services/supportTicketService';
import { TYPE_LABELS, PRIORITY_LABELS, STATUS_CLASSES, statusText, formatDate, ticketContext, ticketProduct, ticketProductPath } from './support/supportLabels';
import './SupportPage.css';
import './PaymentPorterPage.css';

// One support ticket, in hub — every link to a ticket opens here (emails, lists, the Support button).
//
// The support team works the ticket elsewhere; none of that shows here except to a superadmin. The customer sees the
// status (in the team's own words), what they wrote, the conversation — the team's replies and their own — and can
// add more detail, which reaches the team straight away. A ticket from before the move (hub-only) has no conversation;
// a superadmin sets its status by hand, as before.

const SupportTicketDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();

    const [data, setData] = useState(null);       // { ticket, comments, commentsError, canComment }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [replyError, setReplyError] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await supportTicketService.getTicket(id);
            setData(res.data || null);
            setError(null);
        } catch (e) {
            setError(e?.response?.status === 404 ? 'Ticket not found' : 'Failed to load ticket');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const handleStatusChange = async (status) => {
        setUpdating(true);
        try {
            const res = await supportTicketService.updateStatus(id, status);
            setData((d) => ({ ...d, ticket: res.data?.ticket || d.ticket }));
        } catch (e) {
            setError(e?.response?.data?.error?.message || 'Failed to update ticket status');
        } finally {
            setUpdating(false);
        }
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        setReplyError(null);
        try {
            await supportTicketService.addComment(id, reply.trim());
            setReply('');
            await load();
        } catch (e) {
            setReplyError(e?.response?.data?.error?.message || 'Your reply could not be sent — try again');
        } finally {
            setSending(false);
        }
    };

    const ticket = data?.ticket;

    return (
        <div className="support-page">
            <button className="pp-back-btn" onClick={() => navigate('/support')}>← Back to Support</button>

            {loading && !data ? (
                <div className="support-loading">Loading ticket...</div>
            ) : error ? (
                <div className="support-error">{error}</div>
            ) : !ticket ? (
                <div className="support-empty"><p>Ticket not found</p></div>
            ) : (
                <div className="support-detail-card">
                    <div className="support-detail-header">
                        <div>
                            <div className="support-detail-id">Ticket #{ticket.id}</div>
                            <h2 className="support-detail-subject">{ticket.subject}</h2>
                        </div>
                        <span className={`badge ${STATUS_CLASSES[ticket.status] || ''}`}>{statusText(ticket)}</span>
                    </div>

                    <div className="support-detail-meta">
                        <div className="support-detail-meta-item">
                            <span className="support-detail-meta-label">Type</span>
                            <span className="badge badge-type">{TYPE_LABELS[ticket.type] || ticket.type}</span>
                        </div>
                        <div className="support-detail-meta-item">
                            <span className="support-detail-meta-label">Priority</span>
                            <span className={`badge badge-priority-${ticket.priority}`}>{PRIORITY_LABELS[ticket.priority] || ticket.priority}</span>
                        </div>
                        {ticketProduct(ticket) && (
                            <div className="support-detail-meta-item">
                                <span className="support-detail-meta-label">Product</span>
                                <span className="support-detail-meta-value">
                                    {ticketProductPath(ticket)
                                        ? <Link to={ticketProductPath(ticket)}>{ticketProduct(ticket)}</Link>
                                        : ticketProduct(ticket)}
                                </span>
                            </div>
                        )}
                        <div className="support-detail-meta-item">
                            <span className="support-detail-meta-label">Relates to</span>
                            <span className="support-detail-meta-value">{ticketContext(ticket)}</span>
                        </div>
                        {ticket.assignee && (
                            <div className="support-detail-meta-item">
                                <span className="support-detail-meta-label">Assigned to</span>
                                <span className="support-detail-meta-value">{ticket.assignee}</span>
                            </div>
                        )}
                        <div className="support-detail-meta-item">
                            <span className="support-detail-meta-label">Raised by</span>
                            <span className="support-detail-meta-value">{ticket.created_by_name || ticket.created_by_email || '—'}</span>
                        </div>
                        <div className="support-detail-meta-item">
                            <span className="support-detail-meta-label">Created</span>
                            <span className="support-detail-meta-value">{formatDate(ticket.created_at)}</span>
                        </div>
                        {ticket.resolved_at && (
                            <div className="support-detail-meta-item">
                                <span className="support-detail-meta-label">Resolved</span>
                                <span className="support-detail-meta-value">{formatDate(ticket.resolved_at)}</span>
                            </div>
                        )}
                        {isSuperAdmin && ticket.jira_key && (
                            <div className="support-detail-meta-item sa-only">
                                <span className="support-detail-meta-label">Jira</span>
                                <a className="support-detail-meta-value" href={ticket.jira_url} target="_blank" rel="noreferrer">{ticket.jira_key}</a>
                            </div>
                        )}
                    </div>
                    {data.liveError && <div className="support-error">{data.liveError}</div>}
                    {isSuperAdmin && ticket.jira_error && (
                        <div className="support-error sa-only">Not yet in Jira (retried every 5 minutes): {ticket.jira_error}</div>
                    )}

                    <div className="support-detail-section-label">Description</div>
                    <div className="support-detail-description">{ticket.description}</div>

                    {data.canComment && (
                        <div className="support-conversation">
                            <div className="support-detail-section-label">Conversation</div>
                            {data.commentsError && <div className="support-error">{data.commentsError}</div>}
                            {!data.comments.length && !data.commentsError && (
                                <p className="support-subtitle">No replies yet. We will email you when there is news.</p>
                            )}
                            {data.comments.map((c) => (
                                // AN INTERNAL NOTE (Jira's 🔒) reaches superadmins only — the server leaves it out for anyone
                                // else — and is marked as the desk marks it, so nobody mistakes it for a reply the customer saw.
                                <div key={c.id} className={`support-comment${c.fromCustomer ? ' from-customer' : ''}${c.internal ? ' is-internal sa-only' : ''}`}>
                                    <div className="support-comment-head">
                                        <strong>{c.author}{c.internal && <span className="support-comment-internal">Internal note — not shown to the customer</span>}</strong>
                                        <span>{formatDate(c.created)}</span>
                                    </div>
                                    <div className="support-comment-text">{c.text}</div>
                                </div>
                            ))}
                            <div className="support-reply">
                                <textarea placeholder="Add more detail, or reply to the support team…" value={reply}
                                    onChange={(e) => setReply(e.target.value)} disabled={sending} />
                                {replyError && <div className="support-form-error">{replyError}</div>}
                                <div className="support-reply-actions">
                                    <button type="button" className="btn btn-primary" onClick={sendReply} disabled={sending || !reply.trim()}>
                                        {sending ? 'Sending…' : 'Send'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {isSuperAdmin && ticket.jira_sync === 'hub-only' && (
                        <div className="support-detail-status-row sa-only-panel">
                            <label>Update status</label>
                            <select value={ticket.status} disabled={updating} onChange={(e) => handleStatusChange(e.target.value)} className="status-select">
                                <option value="open">Open</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </select>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SupportTicketDetailPage;
